import React, {useEffect, useRef, useState} from 'react';
import {useAuth} from "../../utils/auth.context";
import {
    addFile,
    deleteFile,
    editFile,
    getFilesNoValue,
    getProjectFiles,
    getProjectLocales
} from "../../services/apiService";
import '../../css/FilesBlock.css';
import config from "../../config";
import {getCurrentUser} from "../../services/authService";
import {t} from "../../components/Translate";
import {toast} from "react-toastify";
import EditIcon from '../../assets/img/edit.png'
import DeleteIcon from '../../assets/img/delete.png'
import DownloadIcon from '../../assets/img/download.png'
import GetIcon from '../../assets/img/get.png'
import FileButtons from "./files/FileButtons";
import FileForm from "./files/FileForm";
import FileSearch from "./files/FileSearch";
import FilesTable from "./files/FilesTable";
import DeleteModal from "../../components/DeleteModal";
import Tooltip from "../../components/Tooltip";

const API_URL = process.env.REACT_APP_API_URL;
const currentUser = getCurrentUser() ? getCurrentUser() : {language: 'en'};


const FilesBlock = ({project}) => {
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [files, setFiles] = useState([]);
    const [locales, setLocales] = useState([]);
    const [newFile, setNewFile] = useState({keyName: '', file: {}});
    const [editingFileKeyName, setEditingFileKeyName] = useState(null);
    const [editedFile, setEditedFile] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [inputError, setInputError] = useState('');
    const [filesWithoutValue, setFilesWithoutValue] = useState([]);
    const [description, setDescription] = useState('');
    const [addOrEditState, setAddOrEditState] = useState(false);
    const {user} = useAuth();
    const fileInputRefs = useRef({});
    const imageRefs = useRef({});
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteModalFileKey, setDeleteModalFileKey] = useState();


    useEffect(() => {
        getFilesList();
        getProjectLocalesList();
        getFilesNoValueList();
    }, [project, addOrEditState]);

    const openDeleteModal = (keyName) => {
        setIsDeleteModalOpen(true);
        setDeleteModalFileKey(keyName)
    }

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setDeleteModalFileKey(null)
    }

    const handleDeleteClick = async (keyName) => {
        try {
            await deleteFile(project.id, keyName);
            setFiles((prevFiles) => {
                const updatedFiles = {...prevFiles};
                delete updatedFiles[keyName];
                return updatedFiles;
            });
        } catch (error) {
            console.error(error);
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const confirmFileDelete = async (keyName) => {
        try {
            await deleteFile(project.id, keyName);
            setFiles(prevFiles => {
                const newFiles = {...prevFiles};
                delete newFiles[keyName];
                return newFiles;
            });
            closeDeleteModal();
        } catch (error) {
            console.error(error);
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
            closeDeleteModal();
        }
    };

    const getProjectLocalesList = async () => {
        try {
            setIsLoading(true);
            const data = await getProjectLocales(project.name);
            setLocales(data);
        } catch (error) {
            console.error(error);
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        } finally {
            setIsLoading(false);
        }
    };

    const getFilesList = async () => {
        try {
            const data = await getProjectFiles(project.id);
            setFiles(data);
        } catch (error) {
            console.error(error);
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const getFilesNoValueList = async () => {
        try {
            const data = await getFilesNoValue(project.id);
            setFilesWithoutValue(data);
        } catch (error) {
            console.error(error);
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const handleInputChange = (e) => {
        const regex = /^[a-zA-Z0-9_]*$/;

        const {name, value} = e.target;
        if (editingFileKeyName) {
            setEditedFile({
                ...editedFile,
                [name]: value
            });
        } else {
            if (name === 'keyName') {
                if (!regex.test(value)) {
                    setInputError(t(currentUser.language, "newKeyErrorMessage"));
                } else {
                    setInputError('');
                }
                setNewFile({
                    ...newFile,
                    keyName: value
                });
            }
        }
    };

    const handleDescriptionChange = (e) => {
        setDescription(e.target.value)
    }

    const handleFileChange = (e, locale) => {
        setNewFile({
            ...newFile,
            file: {
                ...newFile.file,
                [locale]: e.target.files[0]
            }
        });
    };

    const getFileByLocale = (keyName, locale) => {
        const filteredFile = files[keyName]?.find(item => item.locale === locale);
        return filteredFile ? filteredFile.path : null;
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value.toLowerCase());
    };


    const filteredFiles = Object.keys(files).filter(keyName => {
        const description = filesWithoutValue?.find(r => r.keyName === keyName)?.description;
        const descriptionMatches = description !== null && description !== undefined ? description.toLowerCase().includes(searchQuery) : false;
        return keyName.toLowerCase().includes(searchQuery) || descriptionMatches;
    });

    const paginatedResources = filteredFiles.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const formatPreview = (items) => {
        return items.map(item => (
            <div key={item.locale} className="locale-preview files-l-p">
                <div className="locale-circle">
                    {item.locale}
                </div>
                <a target="_blank"
                   rel="noopener noreferrer"
                   href={item.path}>
                    <img className={'file-preview-img'} width={25} height={30} src={GetIcon}/>
                </a>
                <br/>
                <a target="_blank"
                   rel="noopener noreferrer"
                   href={item.path.split('files')[0] + 'files/download' + item.path.split('files')[1]}>
                    <img className={'file-preview-img'} width={25} height={25} src={DownloadIcon}/>
                </a>
            </div>
        ));
    };


    const rows = paginatedResources.map(keyName => {
        const items = files[keyName];
        const preview = formatPreview(items);


        return (
            <tr key={keyName}>
                <td onClick={() => {
                    navigator.clipboard.writeText(keyName);
                    toast.info(t(currentUser.language, 'copied'), {
                        position: "top-right",
                        theme: "light",
                        autoClose: 500,
                    });
                }}
                    style={{width: '30%', padding: '10px'}}>
                    {keyName}
                </td>

                <td style={{width: '40%', padding: '10px'}}>
                    {filesWithoutValue !== {} ? filesWithoutValue?.filter(r => r.keyName === keyName)[0]?.description : ''}
                </td>

                <td style={{width: '20%', padding: '10px'}}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                        {preview}
                    </div>
                </td>

                <td style={{width: '10%', textAlign: 'center', padding: '10px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '28px', justifyContent: 'center'}}>
                        <Tooltip text={t(currentUser.language, "edit")}>
                            <img
                                className="edit-img"
                                onClick={() => handleEditClick(keyName)}
                                width={25}
                                height={25}
                                src={EditIcon}
                                alt="Edit"
                                style={{cursor: 'pointer'}}
                            />
                        </Tooltip>


                        {user.data.role === config.roles.manager && (
                            <Tooltip text={t(currentUser.language, "delete")}>
                                <img
                                    className="delete-img"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openDeleteModal(keyName);
                                    }} width={25}
                                    height={25}
                                    src={DeleteIcon}
                                    alt="Delete"
                                    style={{cursor: 'pointer'}}
                                />
                            </Tooltip>
                        )}
                    </div>
                </td>
            </tr>
        );
    });


    const totalPages = Math.ceil(filteredFiles.length / rowsPerPage);
    const pageNumbers = [];


    for (let i = Math.max(1, currentPage - 1); i <= Math.min(currentPage + 1, totalPages); i++) {
        pageNumbers.push(i);
    }

    if (currentPage > 2) {
        pageNumbers.unshift('...');
        pageNumbers.unshift(1);
    }

    if (currentPage < totalPages - 1) {
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
    }

    const handlePageClick = (pageNumber) => {
        if (pageNumber === '...') return;
        setCurrentPage(pageNumber);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };


    const handleEditClick = (keyName) => {
        setDescription(filesWithoutValue !== {} ? filesWithoutValue?.filter(r => r.keyName === keyName)[0]?.description : '')

        setEditingFileKeyName(keyName);
        setIsFormVisible(true);

        Object.values(fileInputRefs.current).forEach(input => {
            if (input) {
                input.value = '';
            }
        });

        locales.forEach(locale => {
            const imgSrc = getFileByLocale(keyName, locale.locale);
            if (imageRefs.current[locale.locale] && imgSrc) {
                imageRefs.current[locale.locale].src = imgSrc;
            }
        });
    };

    const handleSaveEdit = async () => {
        try {
            await editFile(project.id, editingFileKeyName, false, newFile.file, description);
            setIsFormVisible(false);
            setAddOrEditState(!addOrEditState);
            locales.forEach(locale => {
                const imgSrc = getFileByLocale(editingFileKeyName, locale.locale);
                if (imageRefs.current[locale.locale] && imgSrc) {
                    imageRefs.current[locale.locale].src = '';
                }
            });
            setNewFile({keyName: '', file: {}})
        } catch (error) {
            console.error(error);
            if (error.response && error.response.status === 413) {
                toast.error(t(currentUser.language, 'payloadTooLarge'), {position: "top-right", theme: "colored"});
            } else {
                toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
            }
        }
    };

    const toggleFormVisibility = () => {
        setDescription('');
        setIsFormVisible(!isFormVisible);
        setNewFile({keyName: '', file: {}});
        if (isFormVisible) {
            setNewFile({keyName: '', file: {}});
            setEditingFileKeyName(null);

            locales.forEach(locale => {
                const imgSrc = getFileByLocale(editingFileKeyName, locale.locale);
                if (imageRefs.current[locale.locale] && imgSrc) {
                    imageRefs.current[locale.locale].src = '';
                }
            });

            Object.values(fileInputRefs.current).forEach(input => {
                if (input) {
                    input.value = '';
                }
            });
        }
    };

    const handleAddFile = async () => {
        if (!newFile.keyName || !Object.keys(newFile.file).length) {
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
            return;
        }
        try {
            await addFile(project.id, newFile.keyName, false, newFile.file, description);
            setAddOrEditState(!addOrEditState);
            setIsFormVisible(false);
        } catch (error) {
            console.error(error);

            if (error.response && error.response.status === 413) {
                toast.error(t(currentUser.language, 'payloadTooLarge'), {position: "top-right", theme: "colored"});
            } else {
                toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
            }
        }
    };


    return (
        <div>
            <FileButtons
                isFormVisible={isFormVisible}
                toggleFormVisibility={toggleFormVisibility}
                user={user}
                config={config}
                API_URL={API_URL}
                project={project}
                currentUser={currentUser}
            />

            <FileForm
                handleDescriptionChange={handleDescriptionChange}
                description={description}
                isFormVisible={isFormVisible}
                toggleFormVisibility={toggleFormVisibility}
                editingFileKeyName={editingFileKeyName}
                newFile={newFile}
                handleInputChange={handleInputChange}
                inputError={inputError}
                locales={locales}
                fileInputRefs={fileInputRefs}
                handleFileChange={handleFileChange}
                imageRefs={imageRefs}
                currentUser={currentUser}
                handleSaveEdit={handleSaveEdit}
                handleAddFile={handleAddFile}
            />

            <FileSearch
                searchQuery={searchQuery}
                handleSearchChange={handleSearchChange}
                currentUser={currentUser}
            />

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={() => confirmFileDelete(deleteModalFileKey)}
            />

            <FilesTable
                isLoading={isLoading}
                rows={rows}
                currentUser={currentUser}
                currentPage={currentPage}
                pageNumbers={pageNumbers}
                handleNextPage={handleNextPage}
                handlePageClick={handlePageClick}
                handlePreviousPage={handlePreviousPage}
                handleRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPage={rowsPerPage}
                totalPages={totalPages}
                filteredFiles={filteredFiles}
            />
        </div>
    );
};

export default FilesBlock;
